import './EditCardDetails.scss';
import { CardElement, Elements, RecurlyProvider, useRecurly } from '@recurly/react-recurly';
import { Input, Modal } from '@trackman/web-shared-components';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trimTooManySpaces } from 'utilities/inputValidation';
import { ReactComponent as ErrorIcon } from 'images/icons/Error.svg';
import { RecurlyError } from '@recurly/recurly-js';
import { useServices } from 'index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getErrorMessage } from 'utilities/ErrorHelper';
import { BillingInfo, UpdateCreditCardResponse } from 'types/billing';

export const EditCardDetails = ({ isOpen, billingInfo, close }: { isOpen: boolean; billingInfo: BillingInfo; close: () => void }) => {
  return (
    <RecurlyProvider publicKey={process.env.REACT_APP_RECURLY_PUBLIC_KEY}>
      <Elements>
        <EditCardDetailsModal isOpen={isOpen} billingInfo={billingInfo} close={close} />
      </Elements>
    </RecurlyProvider>
  );
};

const EditCardDetailsModal = ({ isOpen, billingInfo, close }: { isOpen: boolean; billingInfo: BillingInfo; close: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const billingService = useServices().billingService;
  const recurly = useRecurly();

  const formRef = useRef<HTMLFormElement>(null);
  const cardTokenIdRef = useRef<string>('');
  const threeDSecureRef = useRef<any>(null);

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [processingPayment, setProcessingPayment] = useState<boolean>(false);

  const handleClose = () => {
    threeDSecureRef.current = null;
    close();
  };

  const { mutate: updateCreditCard } = useMutation({
    mutationFn: (cardTokenId: string) => billingService.updateCreditCardV2(cardTokenId),
    onSuccess: (response) => handleUpdateCreditCardResponse(response),
    onError: (ex: Error) => {
      setErrorMessage(getErrorMessage(ex));
      setProcessingPayment(false);
    },
  });

  const handleUpdateCreditCardResponse = async (
    response: UpdateCreditCardResponse<'tokenReturned'> | UpdateCreditCardResponse<'success'> | UpdateCreditCardResponse<'error'>
  ) => {
    if (response?.status === 'tokenReturned') {
      handleThreeDSecure(response.token);
    } else if (response?.status === 'success') {
      await onEditCardDetailsProcessSuccess();
      close();
    } else {
      setErrorMessage(response.error);
      setProcessingPayment(false);
    }
  };

  const onRecurlyValidationError = (err: RecurlyError) => {
    if (err.fields) {
      setErrorMessage(`${err.message} The following fields appear to be invalid: ${err.fields.join(', ')}`);
    } else if (err && err.message) {
      setErrorMessage(err.message);
    }
  };

  const onEditCardDetailsProcessSuccess = async () => {
    setProcessingPayment(false);
    setFirstName('');
    setLastName('');
    await queryClient.invalidateQueries({ queryKey: ['userBillingInfo'] });
  };

  const handleThreeDSecure = (actionTokenId: string) => {
    const threeDSecure = recurly.Risk().ThreeDSecure({ actionTokenId: actionTokenId });
    threeDSecureRef.current = threeDSecure;

    threeDSecure.on('error', (err) => {
      if (!threeDSecureRef.current) return;
      setErrorMessage(err.message);
      setProcessingPayment(false);
    });

    threeDSecure.on('token', async (actionToken) => {
      if (!threeDSecureRef.current) return;
      await sendThreeDSecureRequest(cardTokenIdRef.current, actionToken.id);
      handleClose();
    });

    threeDSecure.attach(document.body);
  };

  const sendThreeDSecureRequest = async (cardTokenId: string, actionTokenId: string) => {
    try {
      await billingService.sendThreeDSecureRequest(cardTokenId, actionTokenId);
      await onEditCardDetailsProcessSuccess();
    } catch (ex) {
      setErrorMessage(getErrorMessage(ex));
    }
    setProcessingPayment(false);
  };

  const confirm = (event) => {
    if (event.preventDefault) {
      event.preventDefault();
    }

    setProcessingPayment(true);

    formRef.current &&
      recurly.token(formRef.current, async (err, token) => {
        if (err) {
          onRecurlyValidationError(err);
          setProcessingPayment(false);
        } else {
          cardTokenIdRef.current = token.id;
          updateCreditCard(token.id);
        }
      });
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      className='profile-modal edit-payment-method-modal'
      title={t('ChangeCreditCard', { ns: 'billingInformation' })}
      cancelBtn={{
        label: t('Cancel', { ns: 'general' }),
        onClick: handleClose,
        className: 'styled-button secondary',
      }}
      confirmBtn={{
        label: t('SaveChanges', { ns: 'editProfile' }),
        onClick: (e) => confirm(e),
        disabled: processingPayment || !firstName || !lastName,
      }}
    >
      <div className='modal-body'>
        <div className='profile-section-title profile-section-title--secondary'>{t('CardInformation', { ns: 'billingInformation' })}</div>
        <form ref={formRef}>
          <div className='card-holder-data'>
            <Input
              className='card-holder-data-input'
              placeholder={t('FirstName', { ns: 'aboutCertification' })}
              required
              inputProps={{ 'data-recurly': 'first_name' }}
              value={firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(trimTooManySpaces(e.currentTarget.value))}
            />
            <Input
              className='card-holder-data-input'
              placeholder={t('LastName', { ns: 'aboutCertification' })}
              required
              inputProps={{ 'data-recurly': 'last_name' }}
              value={lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(trimTooManySpaces(e.currentTarget.value))}
            />
            <BillingDataForPaymentProcessing
              street1={billingInfo.address.street1}
              postalCode={billingInfo.address.postal_code}
              city={billingInfo.address.city}
              country={billingInfo.address.country}
              state={billingInfo.address.region ?? ''}
            />
          </div>
          <CardElement
            style={{
              placeholder: {
                content: {
                  cvv: t('CVV', { ns: 'billingInformation' }),
                  expiry: t('MM/YY', { ns: 'billingInformation' }),
                  number: t('CardNumber', { ns: 'billingInformation' }),
                },
              },
            }}
          />
          {errorMessage && (
            <p className='error-info'>
              <ErrorIcon />
              <span>{errorMessage}</span>
            </p>
          )}
        </form>
      </div>
    </Modal>
  );
};

type BillingDataForPaymentProcessingProps = {
  street1: string;
  postalCode: string;
  city: string;
  country: string;
  state: string;
};

const BillingDataForPaymentProcessing = ({ street1, postalCode, city, country, state }: BillingDataForPaymentProcessingProps) => {
  return (
    <>
      <input type='hidden' data-recurly='address1' value={street1} />
      <input type='hidden' data-recurly='postal_code' value={postalCode} />
      <input type='hidden' data-recurly='city' value={city} />
      <input type='hidden' data-recurly='country' value={country} />
      {country === 'US' && <input type='hidden' data-recurly='state' value={state} />}
      {country === 'CA' && <input type='hidden' data-recurly='state' value='Default' />}
    </>
  );
};
